import { EncounterStatusHistory, Extension, Task } from '@medplum/fhirtypes'
import dayjs, { Dayjs } from 'dayjs'
import {
  TASK_ASSOCIATED_INTERVENTION_EXTENSION_URL,
  TASK_CAREGIVER_TASK_INFO_CODE_SYSTEM
} from 'fhir/Task/constants'
import {
  BaseEncounterFragment,
  BaseTaskFragment,
  GetCnaVisit_VisitFragment
} from 'generated/graphql'
import { findExtension, getStringExtensionValue } from 'fhir/utils'
import {
  CNAVisitEncounterEVVStatus,
  EncounterEVVExtensions,
  EncounterEVVExtensionsMap,
  EncounterEVVExtensionTypes,
  EncounterStatusHistoryExtensions
} from 'fhir/Encounter/constants'
import {
  ASTM_SIGNATURE_TYPES,
  SIGNATURE_CODE_SYSTEM_URL,
  SignatureFormat
} from 'fhir/Signature/constants'
import { castToEnum } from 'utils/enum'
import { EncounterEVVType, EVVChecks, InterventionData } from './types'

export const getTaskAsInterventionData = (task: Task | BaseTaskFragment): InterventionData => {
  const interventionExtension = findExtension(
    (task.extension ?? []) as Extension[],
    TASK_ASSOCIATED_INTERVENTION_EXTENSION_URL
  )
  const interventionText = interventionExtension?.valueCoding?.code
    ? interventionExtension?.valueCoding?.display
    : undefined

  return {
    id: task.id as string,
    name: interventionText ?? undefined,
    text:
      task.code?.coding?.find(({ system }) => system === TASK_CAREGIVER_TASK_INFO_CODE_SYSTEM)
        ?.display ?? '',
    status: task.status ?? '',
    statusReason: task.statusReason?.coding?.at(0)?.display ?? '',
    note: task.note?.[0].text ?? ''
  }
}

export const getEvvChecksForVisit = (visit: BaseEncounterFragment): EVVChecks => {
  const evvChecks: EVVChecks = {
    [EncounterEVVType.TIME]: getEvvStatusValueFromExtension(
      visit.extension as Extension[],
      EncounterEVVExtensionsMap[EncounterEVVExtensionTypes.TIME]
    ),
    [EncounterEVVType.SIGNATURE]: getEvvStatusValueFromExtension(
      visit.extension as Extension[],
      EncounterEVVExtensionsMap[EncounterEVVExtensionTypes.SIGNATURE]
    ),
    [EncounterEVVType.TASKS]: getEvvStatusValueFromExtension(
      visit.extension as Extension[],
      EncounterEVVExtensionsMap[EncounterEVVExtensionTypes.TASK_COMPLETION]
    )
  }

  return evvChecks
}

export const getEvvStatusValueFromExtension = (
  extensions: Extension[],
  url: EncounterEVVExtensions
): CNAVisitEncounterEVVStatus => {
  const value =
    (extensions?.find((extension) => (extension.url as unknown as EncounterEVVExtensions) === url)
      ?.valueString as CNAVisitEncounterEVVStatus) ?? CNAVisitEncounterEVVStatus.REJECTED
  return value
}

export const getLatestEncounterHistoryExtensionValue = (
  encounterStatusHistory: EncounterStatusHistory[],
  url: EncounterStatusHistoryExtensions
): string => {
  const sortedStatusHistory = [...encounterStatusHistory]?.sort((entryA, entryB) =>
    dayjs(entryB?.period?.start).diff(dayjs(entryA?.period?.start))
  )
  return getStringExtensionValue(sortedStatusHistory?.at(0)?.extension, url) ?? ''
}

interface SignatureData {
  whoName: string
  when: Dayjs
  sigFormat?: SignatureFormat
}

export const getSignatureDataForVisit = (
  visit: GetCnaVisit_VisitFragment
): SignatureData | undefined => {
  const signatureDataList = visit.interventions
    ?.flatMap((task) =>
      task?.output?.flatMap((output): SignatureData | null => {
        const signatureOutput = output.type?.coding?.find(
          (coding) =>
            coding?.system === SIGNATURE_CODE_SYSTEM_URL &&
            // The reason we have to replace all periods is because Medplum automatically
            // sanitizes the codes to not include periods.
            (coding?.code === ASTM_SIGNATURE_TYPES.VALIDATION.replaceAll(/\./g, '') ||
              coding?.code === ASTM_SIGNATURE_TYPES.VERIFICATION.replaceAll(/\./g, ''))
        )
        if (!signatureOutput) return null

        const whoName = output.valueSignature?.who?.display ?? undefined
        const sigFormat = castToEnum(SignatureFormat, output.valueSignature?.sigFormat ?? undefined)
        const whenText = output.valueSignature?.when
        const when = whenText ? dayjs(whenText) : undefined
        if (!whoName || !when) return null

        return { whoName, sigFormat, when }
      })
    )
    ?.filter((signatureData) => !!signatureData)

  return signatureDataList?.at(0) ?? undefined
}
